import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Search, Calendar, MapPin, Music, Ticket, Filter, X, Users,
  ChevronDown, SlidersHorizontal, Mic2,
} from "lucide-react";
import { ArtistCreateRedirectFab } from "@/components/ArtistCreateRedirectFab";

export default function BilletteriePage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    q: "", city: "", genre: "", date_from: "", date_to: "",
    price_min: "", price_max: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  const fetchEvents = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const cleanParams = {};
      Object.entries(params).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) cleanParams[k] = v;
      });
      const res = await api.get("/tickets/search", { params: { ...cleanParams, limit: 30 } });
      setEvents(res.data.events || []);
    } catch {
      setEvents([]);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchEvents(appliedFilters);
  }, [fetchEvents, appliedFilters]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    const empty = { q: "", city: "", genre: "", date_from: "", date_to: "", price_min: "", price_max: "" };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const hasActiveFilters = Object.values(appliedFilters).some(v => v !== "" && v);

  const formatPrice = (cents) => {
    if (cents === 0) return "Gratuit";
    return `${(cents / 100).toFixed(2)} EUR`;
  };

  return (
    <div className="py-6 space-y-6" data-testid="billetterie-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-extrabold text-2xl text-foreground flex items-center gap-2">
          <Ticket className="w-7 h-7 text-[#FF6B35]" />
          Billetterie
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Trouve et reserve tes places de concert</p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Artiste, concert, festival..."
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="pl-10 bg-card border-border rounded-full text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-12"
              data-testid="billetterie-search-input"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-4 h-12 rounded-full border transition-colors ${
              showFilters || hasActiveFilters
                ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                : "bg-card border-border text-muted-foreground"
            }`}
            data-testid="billetterie-filter-btn"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
          <button
            type="submit"
            className="bg-[#FF6B35] text-white font-bold px-6 h-12 rounded-full hover:scale-105 active:scale-95 transition-transform"
            data-testid="billetterie-search-btn"
          >
            <Search className="w-5 h-5" />
          </button>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid="billetterie-filters">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Ville</p>
                <Input
                  placeholder="Paris, Lyon..."
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="filter-city"
                />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1 flex items-center gap-1"><Music className="w-3 h-3" /> Genre</p>
                <Input
                  placeholder="Rap, Rock, Jazz..."
                  value={filters.genre}
                  onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="filter-genre"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Du</p>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:border-[#FF6B35] outline-none h-10"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Au</p>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:border-[#FF6B35] outline-none h-10"
                  data-testid="filter-date-to"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Prix min (EUR)</p>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.price_min}
                  onChange={(e) => setFilters({ ...filters, price_min: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="filter-price-min"
                />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Prix max (EUR)</p>
                <Input
                  type="number"
                  placeholder="200"
                  value={filters.price_max}
                  onChange={(e) => setFilters({ ...filters, price_max: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="filter-price-max"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Effacer
              </button>
            </div>
          </div>
        )}
      </form>

      {/* Results */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="bg-card rounded-3xl h-36 animate-pulse" />)}
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16">
          <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">Aucun evenement avec billetterie</p>
          <p className="text-sm text-muted-foreground mt-1">
            {hasActiveFilters ? "Essaie d'elargir tes criteres" : "Les artistes n'ont pas encore cree de billets"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{events.length} evenement{events.length > 1 ? "s" : ""}</p>
          {events.map((e) => (
            <div
              key={e.id}
              className="bg-card border border-border rounded-3xl overflow-hidden hover:border-[#FF6B35]/30 transition-all duration-300 cursor-pointer shadow-sm"
              onClick={() => navigate(`/events/${e.id}`)}
              data-testid={`billetterie-event-${e.id}`}
            >
              {e.cover_url && (
                <div className="h-28 relative">
                  <img src={e.cover_url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-4">
                    <h3 className="font-heading font-bold text-white text-lg">{e.title}</h3>
                  </div>
                  {e.total_available > 0 ? (
                    <span className="absolute top-3 right-3 bg-[#FF6B35] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {e.total_available} places
                    </span>
                  ) : (
                    <span className="absolute top-3 right-3 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      COMPLET
                    </span>
                  )}
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {/* Date */}
                  <div className="bg-muted rounded-2xl p-3 text-center shrink-0 border border-border min-w-[56px]">
                    <p className="text-[10px] text-muted-foreground uppercase">
                      {new Date(e.date).toLocaleDateString("fr-FR", { month: "short" })}
                    </p>
                    <p className="font-heading font-extrabold text-2xl text-foreground -mt-0.5">
                      {new Date(e.date).getDate()}
                    </p>
                    {e.time && <p className="text-[10px] text-[#FF6B35] font-semibold mt-0.5">{e.time}</p>}
                  </div>

                  <div className="flex-1 min-w-0">
                    {!e.cover_url && (
                      <h3 className="font-heading font-bold text-foreground truncate">{e.title}</h3>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mic2 className="w-3 h-3" /> {e.artist_name}
                      </span>
                      {e.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {e.location}
                        </span>
                      )}
                    </div>
                    {/* Price range */}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center gap-1 bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold px-2.5 py-1 rounded-full border border-[#FF6B35]/20">
                        <Ticket className="w-3 h-3" />
                        {e.min_price === e.max_price
                          ? formatPrice(e.min_price)
                          : `${formatPrice(e.min_price)} - ${formatPrice(e.max_price)}`}
                      </span>
                      {!e.cover_url && e.total_available > 0 && (
                        <span className="text-[10px] text-muted-foreground">
                          {e.total_available} places restantes
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={(ev) => { ev.stopPropagation(); navigate(`/events/${e.id}`); }}
                    className="bg-[#FF6B35] text-white text-xs font-bold px-4 py-2 rounded-full shadow-[0_0_15px_rgba(255,107,53,0.3)] hover:scale-105 active:scale-95 transition-transform shrink-0"
                    data-testid={`buy-ticket-${e.id}`}
                  >
                    Reserver
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ArtistCreateRedirectFab to="/events?create=1" label="Creer une billetterie" testId="create-ticket-fab" />
    </div>
  );
}
